import {useCallback, useMemo, useRef, useState} from 'react';
import Form, { ButtonItem, ButtonOptions, Item, Label } from "devextreme-react/form";
import LoadIndicator from "devextreme-react/load-indicator";
import { useAuth } from "../../../../contexts/auth";
import { FieldDataChangedEvent } from "devextreme/ui/form";
import { LoginCompany } from "../types";
import { useApiLoginCompanies } from "../api/get-login-companies";
import { Template } from 'devextreme-react/core/template';
import {queryClient} from "../../../../lib/react-query";
import {useNavigate} from "react-router-dom";
import './LoginCompanySelectorForm.scss';

const companyTypes = [
    { Name: 'CFI', ID: 1 },
    { Name: 'Dealer', ID: 2 },
    { Name: 'Distributor', ID: 3 },
    { Name: 'Manufacturer', ID: 4 },
    { Name: 'Service Provider', ID: 5 }
]

const renderItem = (data: LoginCompany) => {
    return (
        <>
            <span style={{ "fontWeight": "bold" }}>{data.code}</span>
            &nbsp;{data.name}
        </>
    );
}

export default function LoginCompanySelectorForm() {
    const { changeCompany } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [selectedType, setSelectedType] = useState(1);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const formData = useRef({ companyType: 1, password: '', rememberMe: false, company: undefined });
    const apiCompanies = useApiLoginCompanies();
    const filteredCompanies = useMemo(() => apiCompanies.data.filter(c => c.typeId === selectedType), [apiCompanies.isLoading, selectedType])
    const companyIdEditorOptions = {
        dataSource: { store: filteredCompanies, paginate: true, pageSize: 10 },
        stylingMode: 'outlined',
        labelMode: 'floating',
        placeholder: 'Company',
        displayExpr: 'name',
        searchEnabled: true,
        searchMode: 'contains',
        searchExpr: ['name', 'code'],
        searchTimeoutOption: 200,
        minSearchLengthOption: 0,
        valueExpr: 'id',
        value: selectedCompany,
        showDataBeforeSearchOption: true,
        itemTemplate: 'loginCompanyTemplate'
    };

    const onFieldDataChanged = function onValueChanged(e: FieldDataChangedEvent) {
        if (e.dataField === "companyType") {
            setSelectedCompany(null);
            setSelectedType(e.value);
        }
        if (e.dataField === "company") {
            setSelectedCompany(e.value);
        }
    }

    const onSubmit = useCallback(async (e: any) => {
        e.preventDefault();
        setLoading(true);
        await changeCompany(formData.current.company as any);
        
        setLoading(false);
        await queryClient.removeQueries(['login-companies']);
        let redirectUrl = localStorage.getItem("RedirectUrl");
        if (redirectUrl)
        {
            localStorage.removeItem("RedirectUrl");
            navigate(redirectUrl);
        }
    }, [selectedCompany]);

    return (
        <form className={'company-selector-form'} onSubmit={onSubmit}>
            <Form formData={formData.current} disabled={loading || apiCompanies.isLoading} requiredMark={""} onFieldDataChanged={onFieldDataChanged}>
                <Item
                    dataField={'companyType'}
                    editorType={'dxSelectBox'}
                    editorOptions={companyTypeEditorOptions}
                >
                    <Label visible={false} />
                </Item>
                <Item
                    dataField={'company'}
                    editorType={'dxSelectBox'}
                    editorOptions={companyIdEditorOptions}
                >
                    <Label visible={false} />
                </Item>
                <ButtonItem>
                    <ButtonOptions
                        width={'100%'}
                        type={'default'}
                        useSubmitBehavior={true}
                        disabled={!selectedCompany}
                        elementAttr={{class: 'select-btn'}}
                    >
                        <span className="dx-button-text">
                            {
                                loading
                                    ? <LoadIndicator width={'24px'} height={'24px'} visible={true} />
                                    : 'Select'
                            }
                        </span>
                    </ButtonOptions>
                </ButtonItem>
                <Template name='loginCompanyTemplate' render={renderItem} />
            </Form>
        </form>
    );
}

const companyTypeEditorOptions = {
    stylingMode: 'outlined',
    labelMode: 'floating',
    placeholder: 'Company Type',
    dataSource: companyTypes,
    displayExpr: 'Name',
    searchEnabled: false,
    searchMode: 'contains',
    searchExprOption: 'Name',
    searchTimeoutOption: 200,
    minSearchLengthOption: 0,
    valueExpr: 'ID',
    showDataBeforeSearchOption: true
};
